import type {
    DateSelectorNavigationDateConstantType,
    DateSelectorFirstDayOfWeekConstantType,
    DateSelectorMonthOptionsConstantType,
    DateSelectorMonthsWidthConstantType,
    DateSelectorYearOptionsConstantType,
    DateSelectorRangeResultConstantType,
    DateSelectorWeekNumberConstantType,
    DateSelectorViewChangeConstantType,
    DateSelectorNavigationConstantType,
    DateSelectorWeekDaysConstantType,
    CalendarDaySelectionConstantType,
    DateSelectorTabStopConstantType,
    DateSelectInitialGeneratorType,
    DateSelectorClampConstantType,
    DateSelectorTitleConstantType,
    DateSelectorPageConstantType,
    DateSelectorDaysConstantType,
    DateSelectorRangeResult,
    DateSelectorNavigation,
    CalendarDaySelection,
    DateSelectorWeekDay,
    DayOfWeekLengthType,
    CalendarDay
} from "./type";
import type {
    NativeSelectItem
} from "../nativeSelect/type";
import moment from "moment";
import {
    getMomentLocaleData,
    formatDateTime,
    createMoment
} from "../../helpers/locale/format";
import {
    getHorizontalStep
} from "../../helpers/direction";

export const DATE_SELECTOR_WEEK_DAY_FORMATS: Record<DayOfWeekLengthType, Intl.DateTimeFormatOptions["weekday"]> = {
    "very-short": "narrow",
    short: "short",
    long: "long"
};

export const DATE_SELECTOR_TITLE_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long"
};

export const DATE_SELECTOR_WEEK_DAY_LABEL_FORMAT: Intl.DateTimeFormatOptions = {
    weekday: "long"
};

export const DATE_SELECTOR_DEFAULT_FIRST_DAY_OF_WEEK = 0;

export const DATE_SELECTOR_WEEK_DAY_COUNT = 7;

export const DATE_SELECTOR_WEEK_COUNT = 6;

export const DATE_SELECTOR_MONTH_COUNT = 12;

export const DATE_SELECTOR_ISO_FIRST_DAY_OF_WEEK = 1;

export const DATE_SELECTOR_MONTH_NAME_FORMAT: Intl.DateTimeFormatOptions = {
    month: "long"
};

export const DATE_SELECTOR_YEAR_NAME_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric"
};

export const DATE_SELECTOR_YEARS_BACK = 100;

export const DATE_SELECTOR_YEARS_AHEAD = 10;

export const DATE_SELECTOR_NAVIGATION_KEYS: Array<string> = [
    "ArrowRight",
    "ArrowLeft",
    "ArrowDown",
    "ArrowUp",
    "PageDown",
    "PageUp",
    "Home",
    "End"
];

export const dateSelect = ({
    dateRange,
    variant,
    date
}: DateSelectInitialGeneratorType): Date => {
    if(variant === "single" && date) {
        return date;
    }

    if(variant === "range" && dateRange && (dateRange.start || dateRange.end)) {
        if(dateRange.start) return dateRange.start;
        if(dateRange.end) return dateRange.end;
    }

    return new Date();
};

export const getDateSelectorFirstDayOfWeek = ({
    localeBasedFirstDayOfWeek,
    activeLocale
}: DateSelectorFirstDayOfWeekConstantType): number => {
    if(!localeBasedFirstDayOfWeek) {
        return DATE_SELECTOR_DEFAULT_FIRST_DAY_OF_WEEK;
    }

    return getMomentLocaleData({
        locale: activeLocale
    }).firstDayOfWeek();
};

export const getCalendarDaySelection = ({
    dateRange,
    variant,
    date,
    day
}: CalendarDaySelectionConstantType): CalendarDaySelection => {
    if(variant === "single") {
        return {
            isSelected: !!date && day.isSame(date, "day")
        };
    }

    if(variant !== "range" || !dateRange) {
        return {
            isSelected: false
        };
    }

    const {
        start,
        end
    } = dateRange;

    if(!start || !end || moment(start).isSame(end, "day")) {
        const selectedDate = start ?? end;

        return {
            isSelected: !!selectedDate && day.isSame(selectedDate, "day")
        };
    }

    if(day.isSame(start, "day")) {
        return {
            rangePosition: "start",
            isSelected: false
        };
    }

    if(day.isSame(end, "day")) {
        return {
            rangePosition: "end",
            isSelected: false
        };
    }

    if(day.isBetween(start, end, "day", "()")) {
        return {
            rangePosition: "middle",
            isSelected: false
        };
    }

    return {
        isSelected: false
    };
};

export const getDateSelectorDays = ({
    firstDayOfWeek,
    dateRange,
    viewDate,
    maxDate,
    minDate,
    variant,
    date
}: DateSelectorDaysConstantType): Array<CalendarDay> => {
    const monthStart = moment(viewDate).startOf("month");

    const today = moment();

    const leadingDayCount = (monthStart.day() - firstDayOfWeek + DATE_SELECTOR_WEEK_DAY_COUNT) % DATE_SELECTOR_WEEK_DAY_COUNT;

    const gridStart = monthStart.clone().subtract(leadingDayCount, "days");

    return Array.from({
        length: DATE_SELECTOR_WEEK_DAY_COUNT * DATE_SELECTOR_WEEK_COUNT
    }, (_, index) => {
        const day = gridStart.clone().add(index, "days");

        const selection = getCalendarDaySelection({
            dateRange,
            variant,
            date,
            day
        });

        return {
            isDisabled: (!!minDate && day.isBefore(minDate, "day")) || (!!maxDate && day.isAfter(maxDate, "day")),
            isCurrentMonth: day.isSame(monthStart, "month"),
            rangePosition: selection.rangePosition,
            isToday: day.isSame(today, "day"),
            isSelected: selection.isSelected,
            date: day
        };
    });
};

export const getDateSelectorWeeks = (days: Array<CalendarDay>): Array<Array<CalendarDay>> => {
    return Array.from({
        length: DATE_SELECTOR_WEEK_COUNT
    }, (_, weekIndex) => days.slice(weekIndex * DATE_SELECTOR_WEEK_DAY_COUNT, (weekIndex + 1) * DATE_SELECTOR_WEEK_DAY_COUNT));
};

export const getDateSelectorWeekDays = ({
    dayOfWeekLength,
    activeLocale,
    days
}: DateSelectorWeekDaysConstantType): Array<DateSelectorWeekDay> => {
    return days.slice(0, DATE_SELECTOR_WEEK_DAY_COUNT).map((dayItem) => ({
        shortName: formatDateTime({
            options: {
                weekday: DATE_SELECTOR_WEEK_DAY_FORMATS[dayOfWeekLength]
            },
            date: dayItem.date.toDate(),
            locale: activeLocale
        }),
        longName: formatDateTime({
            options: DATE_SELECTOR_WEEK_DAY_LABEL_FORMAT,
            date: dayItem.date.toDate(),
            locale: activeLocale
        })
    }));
};

export const getDateSelectorTitle = ({
    activeLocale,
    viewDate
}: DateSelectorTitleConstantType): string => {
    return formatDateTime({
        options: DATE_SELECTOR_TITLE_FORMAT,
        locale: activeLocale,
        date: viewDate
    });
};

export const getDateSelectorPageIndex = ({
    viewDate
}: DateSelectorPageConstantType): number => {
    const currentDate = moment(viewDate);

    return currentDate.year() * DATE_SELECTOR_MONTH_COUNT + currentDate.month();
};

export const getDateSelectorNavigation = ({
    viewDate,
    maxDate,
    minDate
}: DateSelectorNavigationConstantType): DateSelectorNavigation => {
    return {
        isPreviousDisabled: !!minDate && !moment(viewDate).isAfter(minDate, "month"),
        isNextDisabled: !!maxDate && !moment(viewDate).isBefore(maxDate, "month")
    };
};

export const getDateSelectorRangeResult = ({
    multipleSelectMinimumRequiredDayCount,
    multipleSelectDayLimit,
    dateRange,
    day
}: DateSelectorRangeResultConstantType): DateSelectorRangeResult => {
    if(!dateRange || !dateRange.start) {
        return {
            dateRange: {
                start: new Date(day.toDate().setHours(0, 0, 0)),
                end: undefined
            }
        };
    }

    if(day.isSame(dateRange.start, "day")) {
        return {
            dateRange: {
                start: undefined,
                end: undefined
            }
        };
    }

    if(dateRange.end) {
        return {
            dateRange: {
                start: day.toDate(),
                end: undefined
            }
        };
    }

    const dayDifference = day.clone().startOf("day").diff(moment(dateRange.start).startOf("day"), "days");

    if(dayDifference < 0) {
        return {
            dateRange: {
                start: new Date(day.toDate().setHours(0, 0, 0)),
                end: undefined
            }
        };
    }

    if(multipleSelectDayLimit && dayDifference >= multipleSelectDayLimit) {
        return {
            errorKey: "maximum-selection-number-of-days-limit-exceeds"
        };
    }

    if(multipleSelectMinimumRequiredDayCount && dayDifference + 1 < multipleSelectMinimumRequiredDayCount) {
        return {
            errorKey: "minimum-selection-number-of-days-required-not-provided"
        };
    }

    return {
        dateRange: {
            end: new Date(day.toDate().setHours(23, 59, 59)),
            start: dateRange.start
        }
    };
};

export const getDateSelectorTabStopIndex = ({
    activeDate,
    days
}: DateSelectorTabStopConstantType): number => {
    const isFocusable = (dayItem: CalendarDay) => dayItem.isCurrentMonth && !dayItem.isDisabled;

    const conditions: Array<(dayItem: CalendarDay) => boolean> = [
        (dayItem) => !!activeDate && dayItem.date.isSame(activeDate, "day"),
        (dayItem) => dayItem.isSelected || dayItem.rangePosition === "start" || dayItem.rangePosition === "end",
        (dayItem) => dayItem.isToday,
        () => true
    ];

    for(const condition of conditions) {
        const index = days.findIndex((dayItem) => isFocusable(dayItem) && condition(dayItem));

        if(index >= 0) {
            return index;
        }
    }

    return -1;
};

export const getDateSelectorNavigationDate = ({
    firstDayOfWeek,
    direction,
    shiftKey,
    maxDate,
    minDate,
    day,
    key
}: DateSelectorNavigationDateConstantType): moment.Moment | undefined => {
    const weekStart = day.clone().subtract((day.day() - firstDayOfWeek + DATE_SELECTOR_WEEK_DAY_COUNT) % DATE_SELECTOR_WEEK_DAY_COUNT, "days");

    const monthStart = day.clone().startOf("month");

    const monthEnd = day.clone().endOf("month").startOf("day");

    const horizontalStep = getHorizontalStep({
        direction,
        key
    });

    const monthUnit = shiftKey ? "years" : "months";

    const targets: Record<string, () => moment.Moment> = {
        End: () => moment.min(weekStart.clone().add(DATE_SELECTOR_WEEK_DAY_COUNT - 1, "days"), monthEnd),
        ArrowUp: () => day.clone().subtract(DATE_SELECTOR_WEEK_DAY_COUNT, "days"),
        ArrowDown: () => day.clone().add(DATE_SELECTOR_WEEK_DAY_COUNT, "days"),
        ArrowRight: () => day.clone().add(horizontalStep, "days"),
        ArrowLeft: () => day.clone().add(horizontalStep, "days"),
        PageUp: () => day.clone().subtract(1, monthUnit),
        Home: () => moment.max(weekStart, monthStart),
        PageDown: () => day.clone().add(1, monthUnit)
    };

    let target = targets[key]?.();

    if(!target) {
        return undefined;
    }

    if(minDate && target.isBefore(minDate, "day")) {
        target = moment(minDate).startOf("day");
    }

    if(maxDate && target.isAfter(maxDate, "day")) {
        target = moment(maxDate).startOf("day");
    }

    return target.isSame(day, "day") ? undefined : target;
};

export const getDateSelectorWeekNumber = ({
    activeLocale,
    days
}: DateSelectorWeekNumberConstantType): number | undefined => {
    const middleDay = days[Math.floor(days.length / 2)];

    if(!middleDay) {
        return undefined;
    }

    const localizedDay = createMoment({
        value: middleDay.date.toDate(),
        locale: activeLocale
    });

    const localeFirstDayOfWeek = getMomentLocaleData({
        locale: activeLocale
    }).firstDayOfWeek();

    if(localeFirstDayOfWeek === DATE_SELECTOR_ISO_FIRST_DAY_OF_WEEK) {
        return localizedDay.isoWeek();
    }

    return localizedDay.week();
};

export const getDateSelectorMonthsWidth = ({
    isShowWeekNumber,
    controlHeights,
    isStretched,
    monthCount,
    spaces
}: DateSelectorMonthsWidthConstantType): number => {
    const dayWidth = isStretched ? controlHeights.small : controlHeights.medium;

    const weekColumnWidth = isShowWeekNumber ? controlHeights.small : 0;

    return (dayWidth * DATE_SELECTOR_WEEK_DAY_COUNT + weekColumnWidth) * monthCount + spaces.spacingLg * (monthCount - 1);
};

export const getDateSelectorVisibleWeeks = (days: Array<CalendarDay>, isTrimmed: boolean): Array<Array<CalendarDay>> => {
    const weeks = getDateSelectorWeeks(days);

    if(!isTrimmed) {
        return weeks;
    }

    return weeks.filter((week) => week.some((dayItem) => dayItem.isCurrentMonth));
};

export const getDateSelectorMonthOptions = ({
    activeLocale,
    viewDate,
    maxDate,
    minDate
}: DateSelectorMonthOptionsConstantType): Array<NativeSelectItem> => {
    const year = moment(viewDate).year();

    const viewMonth = moment(viewDate).month();

    const options: Array<NativeSelectItem> = [];

    for(let month = 0; month < DATE_SELECTOR_MONTH_COUNT; month++) {
        const monthStart = moment([
            year,
            month,
            1
        ]);

        const isOutside = (!!minDate && monthStart.isBefore(minDate, "month")) || (!!maxDate && monthStart.isAfter(maxDate, "month"));

        if(isOutside && month !== viewMonth) {
            continue;
        }

        options.push({
            title: formatDateTime({
                options: DATE_SELECTOR_MONTH_NAME_FORMAT,
                date: monthStart.toDate(),
                locale: activeLocale
            }),
            value: String(month)
        });
    }

    return options;
};

export const getDateSelectorYearOptions = ({
    activeLocale,
    viewDate,
    maxDate,
    minDate
}: DateSelectorYearOptionsConstantType): Array<NativeSelectItem> => {
    const currentYear = moment().year();

    const viewYear = moment(viewDate).year();

    const firstYear = Math.min(minDate ? moment(minDate).year() : currentYear - DATE_SELECTOR_YEARS_BACK, viewYear);

    const lastYear = Math.max(maxDate ? moment(maxDate).year() : currentYear + DATE_SELECTOR_YEARS_AHEAD, viewYear);

    return Array.from({
        length: lastYear - firstYear + 1
    }, (_, index) => {
        const year = firstYear + index;

        return {
            title: formatDateTime({
                options: DATE_SELECTOR_YEAR_NAME_FORMAT,
                date: new Date(year, 0, 1),
                locale: activeLocale
            }),
            value: String(year)
        };
    });
};

export const getDateSelectorClampedViewDate = ({
    monthCount = 1,
    viewDate,
    maxDate,
    minDate
}: DateSelectorClampConstantType): Date => {
    let view = moment(viewDate).startOf("month");

    if(maxDate && view.clone().add(monthCount - 1, "months").isAfter(maxDate, "month")) {
        view = moment(maxDate).startOf("month").subtract(monthCount - 1, "months");
    }

    if(minDate && view.isBefore(minDate, "month")) {
        view = moment(minDate).startOf("month");
    }

    return view.toDate();
};

export const getDateSelectorViewDateForTarget = ({
    monthCount,
    viewDate,
    target
}: DateSelectorViewChangeConstantType): Date | undefined => {
    const firstMonth = moment(viewDate).startOf("month");

    const lastMonth = firstMonth.clone().add(monthCount - 1, "months");

    if(!target.isBefore(firstMonth, "month") && !target.isAfter(lastMonth, "month")) {
        return undefined;
    }

    if(target.isBefore(firstMonth, "month")) {
        return target.clone().startOf("month").toDate();
    }

    return target.clone().startOf("month").subtract(monthCount - 1, "months").toDate();
};
